import {
  useServiceExpenditure,
  useInfrastructureExpenditure,
} from '@src/modules/performance/context/reports-api-hooks';
import {
  Box, Link, Text, Wrap,
} from '@chakra-ui/react';
import { currentDatabaseSelector, selectedDatabaseIdState } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LockYearBadge from '@src/modules/performance/components/LockYearBadge';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Public Transport Expenditure (Regional Only)',
  content: [
    {
      title: 'Public transport service expenditure',
      description:
  <Text fontSize="inherit" mb={2}>
    Expenditure includes for contracted bus, ferry, and rail public transport services that are set out in the current regional public transport plan.
  </Text>,
      source: null,
    },
    {
      title: 'Public transport infrastructure expenditure',
      description:
  <Text fontSize="inherit" mb={2}>
    Expenditure includes:
    <br />
    - the capital cost of all new or improved public transport facilities and , exceeding $1,000,000 per activity, and like-for-like replacement/renewal of public transport facilities and infrastructure of more than $5 million total cost per activity.
    <br />
    - the management, operation and maintenance (including like-for-like replacement up to $5 million total cost per activity) of off-vehicle facilities and equipment associated with the delivery of public transport services separately identified by mode or for multi-modal use.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools, funding - public transport</Link>
  </Text>,
    },
  ],
};

function PublicTransportExpenditure() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: serviceExpenditure, isLoading: isLoadingService } = useServiceExpenditure();
  const { data: infrastructureExpenditure, isLoading: isLoadingInfrastructure } = useInfrastructureExpenditure();
  const isLoading = isLoadingService || isLoadingInfrastructure;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Percentile'];
  }

  // Check if all lockYears are the same
  const allLockYears = [
    serviceExpenditure?.[0]?.LockYear,
    infrastructureExpenditure?.[0]?.LockYear,
  ];
  const primaryLockYear = allLockYears.every((year) => year === allLockYears[0]) ? allLockYears[0] : null;

  // Service Expenditure Chart
  const serviceExpenditureYears = [...new Set(serviceExpenditure?.map((item) => item.Year))];
  const serviceExpenditureData = [{
    name: 'Region',
    type: 'line',
    data: serviceExpenditure?.map((item) => ({ x: item.Year, y: item.Value / 1000000 })),
    color: CHART_COLOURS.blue,
  },
  {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: serviceExpenditure?.map((item) => ({ x: item.Year, y: [item.LowPercentile / 1000000, item.HighPercentile / 1000000] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Infrastructure Expenditure Chart
  const infrastructureExpenditureYears = [...new Set(infrastructureExpenditure?.map((item) => item.Year))];
  const infrastructureExpenditureData = [{
    name: currentDatabase?.name || 'TA',
    type: 'line',
    data: infrastructureExpenditure?.map((item) => ({ x: item.Year, y: item.Value / 1000000 })),
    color: CHART_COLOURS.blue,
  },
  {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: infrastructureExpenditure?.map((item) => ({ x: item.Year, y: [item.LowPercentile / 1000000, item.HighPercentile / 1000000] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const infrastructureExpenditureSeries = filterChartDataByName(
    infrastructureExpenditureData,
    chartFilters,
  );

  const markers = {
    size: [3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = {
    curve: 'straight',
    width: [2, 0],
  };

  return (
    <ReportSection
      title="Public Transport Expenditure"
      isLoading={isLoading || !serviceExpenditure || !infrastructureExpenditure}
      informationSource={informationSource}
      lockYear={primaryLockYear || null}
    >
      <Wrap spacing={0}>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={serviceExpenditure?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title={['(Region)', 'Public transport service expenditure']}
            categories={serviceExpenditureYears}
            series={serviceExpenditureData}
            width="280"
            height="280"
            markers={markers}
            stroke={stroke}
            prefix="$"
            suffix="M"
            decimalPoints={2}
          />
        </Box>
        <Box>
          {!primaryLockYear
          && (
          <LockYearBadge
            justify="flex-start"
            mb={2}
            lockYear={infrastructureExpenditure?.[0]?.LockYear}
          />
          )}
          <LineRangeAreaChart
            title={[`(${currentDatabase?.name})`, 'Public transport infrastructure expenditure']}
            categories={infrastructureExpenditureYears}
            series={infrastructureExpenditureSeries}
            width="280"
            height="280"
            markers={markers}
            stroke={stroke}
            prefix="$"
            suffix="M"
            decimalPoints={2}
          />
        </Box>
      </Wrap>
    </ReportSection>
  );
}

export default PublicTransportExpenditure;
